import { defineStore } from "pinia"
import { ref, computed, type Ref } from "vue"
import { type IWindow, type WindowTypes } from "@/interfaces/component/window.interface"

export const useWindowStore = defineStore("windowStore", () => {
  const windows: Ref<IWindow[]> = ref([])
  const currId = ref(0)

  let maxZIndex = 0

  /**
   * Add a window to the store.
   * @param type - The type of the window.
   * @param props - The properties of the window.
   * @returns The id of the window.
   */
  function addWindow(type: WindowTypes, props: Record<string, any>): number {
    currId.value++
    windows.value.push({
      id: currId.value,
      loaded: false,
      type,
      props
    })
    return currId.value
  }

  /**
   * Close a window.
   * @param id - The id of the window to close.
   */
  function closeWindow(id: number) {
    const index = windows.value.findIndex((w) => w.id === id)
    if (index !== -1) {
      windows.value.splice(index, 1)
    }
  }

  /**
   * Mark a window as loaded.
   * @param id - The id of the window to mark as loaded.
   */
  function loadedWindow(id: number) {
    const index = windows.value.findIndex((w) => w.id === id)
    if (index !== -1) {
      windows.value[index].loaded = true
    }
  }

  /**
   * Check if all windows are loaded.
   * @returns True if all windows are loaded, false otherwise.
   */
  const allWindowsLoaded = computed(() => {
    return windows.value.every((window) => window.loaded)
  })

  function getWindow(id: number) {
    return windows.value.find((w) => w.id === id)
    //
  }

  /**
   * Get the next z-index for a window.
   * @returns The next z-index.
   */
  function nextZIndex(): number {
    if (maxZIndex < currId.value) {
      maxZIndex = currId.value
    }
    return maxZIndex++
  }

  return {
    windows,
    addWindow,
    closeWindow,
    loadedWindow,
    allWindowsLoaded,
    nextZIndex,
    getWindow
  }
})
